import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Factory, Droplets, Scissors, AlertTriangle, CheckCircle, Clock, Package, Truck, FileSpreadsheet, Download, RefreshCw, Filter, ChevronRight, Ship, Trash2, ClipboardCheck, Edit, FileText, Search, Bell } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductionDashboardPage() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [weavingDelays, setWeavingDelays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  const [shippingWO, setShippingWO] = useState(null);
  const [deletingWO, setDeletingWO] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newWONotification, setNewWONotification] = useState(null);

  // Check permissions
  const canMarkShipped = currentUser?.role === 'admin' || currentUser?.department?.toLowerCase() === 'stitching';
  const canDeleteWO = currentUser?.role === 'admin';  // Only admin can delete
  const canEditWO = currentUser?.role === 'admin' || currentUser?.department?.toLowerCase() === 'weaving';
  const canReconcile = currentUser?.role === 'admin' || ['stitching', 'accessories', 'marketing'].includes(currentUser?.department?.toLowerCase());

  useEffect(() => {
    fetchDashboard();
    fetchWeavingDelays();
    fetchCurrentUser();
    fetchNewWONotification();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
  };

  const fetchNewWONotification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/latest-new-wo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.notification) {
        setNewWONotification(response.data.notification);
      }
    } catch (err) {
      console.error("Failed to fetch new WO notification:", err);
    }
  };

  const dismissWONotification = async () => {
    if (!newWONotification) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${newWONotification.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Failed to dismiss notification:", err);
    }
    setNewWONotification(null);
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/production-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeavingDelays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/alerts/weaving-delays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeavingDelays(response.data.alerts || []);
    } catch (err) {
      console.error("Failed to fetch weaving delays:", err);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'on_track':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> On Track</Badge>;
      case 'at_risk':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> At Risk</Badge>;
      case 'delayed':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><AlertTriangle className="w-3 h-3 mr-1" /> Delayed</Badge>;
      case 'shipped':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><Truck className="w-3 h-3 mr-1" /> Shipped</Badge>;
      default:
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><Clock className="w-3 h-3 mr-1" /> Not Started</Badge>;
    }
  };

  const filteredOrders = dashboardData?.work_orders?.filter(order => {
    if (filter === 'on_track' && order.overall_status !== 'on_track') return false;
    if (filter === 'at_risk' && order.overall_status !== 'at_risk') return false;
    if (filter === 'delayed' && order.overall_status !== 'delayed') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (order.wo_number || '').toLowerCase().includes(q) ||
        (order.customer_name || '').toLowerCase().includes(q) ||
        (order.customer_po || '').toLowerCase().includes(q);
    }
    return true;
  }) || [];

  const handleExportDashboard = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/dashboard/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Production_Dashboard_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export:", err);
    } finally {
      setExporting(false);
    }
  };

  // Work Order PDF Export
  const handleExportWOPDF = async (woId, woNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/work-order/${woId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${woNumber}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export PDF:", err);
      alert("Failed to download PDF");
    }
  };

  // Work Order Excel Export
  const handleExportWOExcel = async (woId, woNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/work-order/${woId}/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${woNumber}_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to export Excel:", err);
      alert("Failed to download Excel");
    }
  };

  const handleMarkShipped = async (woId, woNumber) => {
    if (!window.confirm(`Are you sure you want to mark ${woNumber} as SHIPPED? This action cannot be undone.`)) {
      return;
    }
    
    setShippingWO(woId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/work-orders/${woId}/mark-shipped`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`${woNumber} marked as shipped successfully!`);
      fetchDashboard();
    } catch (err) {
      console.error("Failed to mark as shipped:", err);
      alert(err.response?.data?.detail || "Failed to mark as shipped");
    } finally {
      setShippingWO(null);
    }
  };

  const handleMarkReady = async (woId, woNumber) => {
    if (!window.confirm(`Mark ${woNumber} as READY for dispatch?`)) {
      return;
    }
    
    setShippingWO(woId);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/work-orders/${woId}/mark-ready`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`${woNumber} marked as ready!`);
      fetchDashboard();
    } catch (err) {
      console.error("Failed to mark as ready:", err);
      alert(err.response?.data?.detail || "Failed to mark as ready");
    } finally {
      setShippingWO(null);
    }
  };

  const handleDeleteWO = async (woId, woNumber) => {
    if (!window.confirm(`⚠️ DELETE ${woNumber}?\n\nThis will permanently delete:\n- The work order\n- All production entries (weaving, dyeing, stitching)\n- All accessories\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    setDeletingWO(woId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-orders/${woId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${woNumber} deleted successfully!`);
      fetchDashboard(); // Refresh the dashboard
    } catch (err) {
      console.error("Failed to delete work order:", err);
      alert(err.response?.data?.detail || "Failed to delete work order");
    } finally {
      setDeletingWO(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const summary = dashboardData?.summary || { total: 0, on_track: 0, at_risk: 0, delayed: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6" />
            Production Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of all active Work Orders and production status</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchWeavingDelays(); }}>
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="default" size="sm" onClick={handleExportDashboard} disabled={exporting}>
            <FileSpreadsheet className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export Excel'}</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-primary' : 'hover:shadow-md'}`} onClick={() => setFilter('all')}>
          <CardHeader className="pb-2">
            <CardDescription>Total Orders</CardDescription>
            <CardTitle className="text-3xl">{summary.total}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Active work orders</p>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all border-l-4 border-l-green-500 ${filter === 'on_track' ? 'ring-2 ring-green-500' : 'hover:shadow-md'}`} onClick={() => setFilter('on_track')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-green-600">On Track</CardDescription>
            <CardTitle className="text-3xl text-green-600">{summary.on_track}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(summary.on_track / summary.total) * 100 || 0} className="h-1 bg-green-100" />
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all border-l-4 border-l-yellow-500 ${filter === 'at_risk' ? 'ring-2 ring-yellow-500' : 'hover:shadow-md'}`} onClick={() => setFilter('at_risk')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-yellow-600">At Risk</CardDescription>
            <CardTitle className="text-3xl text-yellow-600">{summary.at_risk}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(summary.at_risk / summary.total) * 100 || 0} className="h-1 bg-yellow-100" />
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all border-l-4 border-l-red-500 ${filter === 'delayed' ? 'ring-2 ring-red-500' : 'hover:shadow-md'}`} onClick={() => setFilter('delayed')}>
          <CardHeader className="pb-2">
            <CardDescription className="text-red-600">Delayed</CardDescription>
            <CardTitle className="text-3xl text-red-600">{summary.delayed}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(summary.delayed / summary.total) * 100 || 0} className="h-1 bg-red-100" />
          </CardContent>
        </Card>
      </div>

      {/* Weaving Delay Alerts - BOLD BLACK */}
      {weavingDelays.length > 0 && (
        <Card className="bg-black border-black text-white">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              ⚠️ WEAVING DELAY ALERTS - {weavingDelays.length} WORK ORDER(S) AT RISK
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {weavingDelays.map((alert, idx) => (
              <div key={idx} className={`p-3 rounded-lg ${alert.severity === 'high' ? 'bg-red-900' : 'bg-yellow-900'}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="font-bold text-base">{alert.wo_number}</span>
                    <span className="ml-2 opacity-80">({alert.customer_name})</span>
                  </div>
                  <Badge variant={alert.severity === 'high' ? 'destructive' : 'warning'} className="ml-2">
                    {alert.days_pending} DAYS PENDING
                  </Badge>
                </div>
                <div className="text-sm space-y-1">
                  <p><span className="font-semibold">Total Sizes:</span> {alert.total_sizes}</p>
                  <p><span className="font-semibold text-green-400">Sizes Started:</span> {alert.sizes_started_count > 0 ? (alert.sizes_started || []).join(', ') : 'NONE'}</p>
                  <p><span className="font-semibold text-red-400">Sizes NOT Started:</span> {(alert.sizes_not_started || []).join(', ') || 'N/A'}</p>
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <span className="font-bold text-yellow-400">⚠️ BUSINESS IMPACT:</span>
                    <span className="ml-2">Order at risk of missing shipment deadline ({alert.shipment_date || 'N/A'})</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <CardTitle className="text-lg">Work Orders ({filteredOrders.length})</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search WO, customer, PO..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-56"
                  data-testid="wip-search-bar"
                />
              </div>
              {filter !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
                  <Filter className="w-4 h-4 mr-1" /> Clear Filter
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-hidden">
          {/* Mobile Card View */}
          <div className="block lg:hidden p-4 space-y-4" data-testid="mobile-orders-view">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No work orders found
              </div>
            ) : (
              filteredOrders.map((order) => (
                <Card key={order.id} className="border shadow-sm" data-testid={`mobile-wo-card-${order.wo_number}`}>
                  <CardContent className="p-4 space-y-3">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-lg">{order.wo_number}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                        {order.customer_po_number && (
                          <p className="text-xs text-muted-foreground">PO: {order.customer_po_number}</p>
                        )}
                      </div>
                      {order.current_stage === 'shipped' ? (
                        <Badge className="bg-green-600 text-white"><Ship className="w-3 h-3 mr-1" /> Shipped</Badge>
                      ) : (
                        getStatusBadge(order.overall_status)
                      )}
                    </div>
                    
                    {/* Order Qty */}
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Plan Qty:</span>
                        <span className="font-semibold text-blue-600">{(order.total_plan_pcs || order.total_qty_pcs || 0).toLocaleString()} pcs</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Weaved:</span>
                        <span className="font-medium text-green-600">{(order.weaving?.produced || 0).toLocaleString()} pcs / {order.weaving?.produced_kg || 0} kg</span>
                      </div>
                    </div>
                    
                    {/* Progress Bars */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Factory className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs w-16">Weaving</span>
                        <Progress value={order.weaving?.completion || 0} className="flex-1 h-2" />
                        <span className="text-xs w-10 text-right">{Math.round(order.weaving?.completion || 0)}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs w-16">Dyeing</span>
                        <Progress value={order.dyeing?.completion || 0} className="flex-1 h-2" />
                        <span className="text-xs w-10 text-right">{Math.round(order.dyeing?.completion || 0)}%</span>
                      </div>
                      {order.packing_type === 'roll_form' ? (
                        <div className="flex items-center gap-2">
                          <Scissors className="w-4 h-4 text-blue-400" />
                          <span className="text-xs w-16">Stitching</span>
                          <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-xs">Roll Form - N/A</Badge>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Scissors className="w-4 h-4 text-muted-foreground" />
                          <span className="text-xs w-16">Stitching</span>
                          <Progress value={order.stitching?.completion || 0} className="flex-1 h-2" />
                          <span className="text-xs w-10 text-right">{Math.round(order.stitching?.completion || 0)}%</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Shipment & Actions */}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <div className="text-sm">
                        <p className="text-muted-foreground">Shipment: {order.shipment_date || 'N/A'}</p>
                        <p className="text-xs uppercase text-muted-foreground">
                          {order.current_stage === 'ready' ? (
                            <Badge className="bg-amber-500 text-white text-xs">Ready</Badge>
                          ) : order.current_stage === 'shipped' ? (
                            <Badge className="bg-green-600 text-white text-xs">Shipped</Badge>
                          ) : order.current_stage}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {canMarkShipped && order.current_stage !== 'shipped' && order.current_stage !== 'ready' && (
                          (order.packing_type === 'roll_form' 
                            ? Math.round(order.dyeing?.completion || 0) >= 50 
                            : Math.round(order.stitching?.completion || 0) >= 50
                          ) && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-amber-600 border-amber-300 hover:bg-amber-50 h-9"
                                onClick={() => handleMarkReady(order.id, order.wo_number)}
                                disabled={shippingWO === order.id}
                                data-testid={`mobile-mark-ready-${order.wo_number}`}
                              >
                                {shippingWO === order.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                                <span className="ml-1 text-xs">Ready</span>
                              </Button>
                              <Button 
                                variant="default" 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 h-9"
                                onClick={() => handleMarkShipped(order.id, order.wo_number)}
                                disabled={shippingWO === order.id}
                                data-testid={`mobile-mark-shipped-${order.wo_number}`}
                              >
                                {shippingWO === order.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ship className="w-4 h-4" />}
                                <span className="ml-1 text-xs">Ship</span>
                              </Button>
                            </>
                          )
                        )}
                        {canMarkShipped && order.current_stage === 'ready' && (
                          <Button 
                            variant="default" 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 h-9"
                            onClick={() => handleMarkShipped(order.id, order.wo_number)}
                            disabled={shippingWO === order.id}
                            data-testid={`mobile-mark-shipped-${order.wo_number}`}
                          >
                            {shippingWO === order.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ship className="w-4 h-4" />}
                            <span className="ml-1 text-xs">Ship</span>
                          </Button>
                        )}
                        {canReconcile && (order.current_stage === 'shipped' || order.current_stage === 'ready') && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-blue-600 h-9"
                            onClick={() => navigate(`/reconciliation/${order.id}`)}
                          >
                            <ClipboardCheck className="w-4 h-4" />
                          </Button>
                        )}
                        {canDeleteWO && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 h-9"
                            onClick={() => handleDeleteWO(order.id, order.wo_number)}
                            disabled={deletingWO === order.id}
                          >
                            {deletingWO === order.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        )}
                        {canEditWO && (
                          <Link to={`/create-work-order?edit=${order.wo_number}`}>
                            <Button variant="ghost" size="sm" className="text-blue-600 h-9">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                        {/* Download Buttons - Mobile */}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 h-9"
                          onClick={() => handleExportWOPDF(order.id, order.wo_number)}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-green-600 h-9"
                          onClick={() => handleExportWOExcel(order.id, order.wo_number)}
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        <Link to={`/weaving-production?wo=${order.id}`}>
                          <Button variant="outline" size="sm" className="h-9">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto -mx-0">
            <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-bold whitespace-nowrap">WO #</TableHead>
                <TableHead className="font-bold whitespace-nowrap">Customer</TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">Order Qty</TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 justify-center">
                        <Factory className="w-4 h-4" /> Weaving
                      </TooltipTrigger>
                      <TooltipContent>Weaving Progress</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 justify-center">
                        <Droplets className="w-4 h-4" /> Dyeing
                      </TooltipTrigger>
                      <TooltipContent>Dyeing Progress</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1 justify-center">
                        <Scissors className="w-4 h-4" /> Stitching
                      </TooltipTrigger>
                      <TooltipContent>Stitching Progress</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="font-bold whitespace-nowrap">Shipment</TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">Status</TableHead>
                <TableHead className="font-bold text-center whitespace-nowrap">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No work orders found
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-muted/30">
                    <TableCell className="font-medium">{order.wo_number}</TableCell>
                    <TableCell>
                      <div>{order.customer_name}</div>
                      {order.customer_po_number && (
                        <div className="text-xs text-muted-foreground">PO: {order.customer_po_number}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="font-semibold text-blue-600">{(order.total_plan_pcs || order.total_qty_pcs || 0).toLocaleString()} pcs</div>
                      <div className="text-xs text-muted-foreground">{order.total_qty_weight || 0} kg</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="space-y-1">
                        <Progress value={order.weaving?.completion || 0} className="h-2" />
                        <div className="text-xs">{(order.weaving?.produced || 0).toLocaleString()} ({Math.round(order.weaving?.completion || 0)}%)</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="space-y-1">
                        <Progress value={order.dyeing?.completion || 0} className="h-2" />
                        <div className="text-xs">{(order.dyeing?.produced || 0).toLocaleString()} ({Math.round(order.dyeing?.completion || 0)}%)</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {order.packing_type === 'roll_form' ? (
                        <div className="text-center">
                          <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                            Roll Form
                          </Badge>
                          <div className="text-xs text-muted-foreground mt-1">No Stitching</div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Progress value={order.stitching?.completion || 0} className="h-2" />
                          <div className="text-xs">{(order.stitching?.produced || 0).toLocaleString()} ({Math.round(order.stitching?.completion || 0)}%)</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{order.shipment_date}</div>
                      <div className="text-xs text-muted-foreground uppercase">{order.current_stage}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {order.current_stage === 'shipped' ? (
                        <Badge className="bg-green-600 text-white"><Ship className="w-3 h-3 mr-1" /> Shipped</Badge>
                      ) : order.current_stage === 'ready' ? (
                        <Badge className="bg-amber-500 text-white"><Package className="w-3 h-3 mr-1" /> Ready</Badge>
                      ) : (
                        getStatusBadge(order.overall_status)
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center gap-1 justify-center">
                        {canMarkShipped && order.current_stage !== 'shipped' && order.current_stage !== 'ready' && (
                          (order.packing_type === 'roll_form' 
                            ? Math.round(order.dyeing?.completion || 0) >= 50 
                            : Math.round(order.stitching?.completion || 0) >= 50
                          ) && (
                            <>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-amber-600 border-amber-300 hover:bg-amber-50 min-h-[44px] min-w-[44px]"
                                      onClick={() => handleMarkReady(order.id, order.wo_number)}
                                      disabled={shippingWO === order.id}
                                      data-testid={`mark-ready-${order.wo_number}`}
                                    >
                                      {shippingWO === order.id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Package className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Mark as Ready</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="default" 
                                      size="sm" 
                                      className="bg-green-600 hover:bg-green-700 min-h-[44px] min-w-[44px]"
                                      onClick={() => handleMarkShipped(order.id, order.wo_number)}
                                      disabled={shippingWO === order.id}
                                      data-testid={`mark-shipped-${order.wo_number}`}
                                    >
                                      {shippingWO === order.id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Ship className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Mark as Shipped</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </>
                          )
                        )}
                        {canMarkShipped && order.current_stage === 'ready' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700 min-h-[44px] min-w-[44px]"
                                  onClick={() => handleMarkShipped(order.id, order.wo_number)}
                                  disabled={shippingWO === order.id}
                                  data-testid={`mark-shipped-${order.wo_number}`}
                                >
                                  {shippingWO === order.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Ship className="w-4 h-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Mark as Shipped</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canReconcile && (order.current_stage === 'shipped' || order.current_stage === 'ready') && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 min-h-[44px] min-w-[44px]"
                                  onClick={() => navigate(`/reconciliation/${order.id}`)}
                                  data-testid={`reconcile-${order.wo_number}`}
                                >
                                  <ClipboardCheck className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Reconcile Shipment</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canDeleteWO && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px] min-w-[44px]"
                                  onClick={() => handleDeleteWO(order.id, order.wo_number)}
                                  disabled={deletingWO === order.id}
                                  data-testid={`delete-wo-${order.wo_number}`}
                                >
                                  {deletingWO === order.id ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete Work Order</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {canEditWO && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Link to={`/create-work-order?edit=${order.wo_number}`}>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 min-h-[44px] min-w-[44px]"
                                    data-testid={`edit-wo-${order.wo_number}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent>Edit Work Order</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {/* Download PDF */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px] min-w-[44px]"
                                onClick={() => handleExportWOPDF(order.id, order.wo_number)}
                                data-testid={`download-pdf-${order.wo_number}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Download PDF</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {/* Download Excel */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 min-h-[44px] min-w-[44px]"
                                onClick={() => handleExportWOExcel(order.id, order.wo_number)}
                                data-testid={`download-excel-${order.wo_number}`}
                              >
                                <FileSpreadsheet className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Download Excel</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Link to={`/weaving-production?wo=${order.id}`}>
                          <Button variant="outline" size="sm" className="min-h-[44px] min-w-[44px]">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* New Work Order Notification Popup */}
      <Dialog open={!!newWONotification} onOpenChange={(open) => { if (!open) dismissWONotification(); }}>
        <DialogContent className="max-w-md" data-testid="new-wo-notification-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-amber-500" />
              New Work Order Created
            </DialogTitle>
          </DialogHeader>
          {newWONotification && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground font-medium">WO Number</p>
                  <p className="font-semibold" data-testid="notif-wo-number">{newWONotification.wo_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Customer</p>
                  <p className="font-semibold" data-testid="notif-customer">{newWONotification.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">PO Number</p>
                  <p className="font-semibold" data-testid="notif-po-number">{newWONotification.customer_po_number || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Shipment Date</p>
                  <p className="font-semibold" data-testid="notif-shipment-date">{newWONotification.shipment_date ? new Date(newWONotification.shipment_date).toLocaleDateString('en-IN') : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Total Pcs</p>
                  <p className="font-semibold" data-testid="notif-total-pcs">{newWONotification.total_qty_pcs?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Total Kgs</p>
                  <p className="font-semibold" data-testid="notif-total-kgs">{newWONotification.total_qty_weight?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground pt-1">
                Created by <span className="font-medium">{newWONotification.created_by}</span> on {new Date(newWONotification.created_at).toLocaleString('en-IN')}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={dismissWONotification} className="w-full" data-testid="notif-ok-btn">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
